import { useState, useRef, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Slider } from '@/components/ui/slider';
import { Camera, ZoomIn, ZoomOut, Upload, RotateCcw } from 'lucide-react';

interface AvatarEditorDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSave: (blob: Blob) => Promise<void>;
  currentAvatarUrl?: string | null;
}

const CANVAS_SIZE = 280;
const OUTPUT_SIZE = 512;

export function AvatarEditorDialog({
  open,
  onOpenChange,
  onSave,
  currentAvatarUrl,
}: AvatarEditorDialogProps) {
  const { t } = useTranslation();
  const [image, setImage] = useState<HTMLImageElement | null>(null);
  const [zoom, setZoom] = useState(1);
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  const [dragging, setDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [saving, setSaving] = useState(false);

  const canvasRef = useRef<HTMLCanvasElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const resetState = useCallback(() => {
    setImage(null);
    setZoom(1);
    setOffset({ x: 0, y: 0 });
    setDragging(false);
    setSaving(false);
  }, []);

  // Load current avatar when dialog opens
  useEffect(() => {
    if (open && currentAvatarUrl && !image) {
      const img = new Image();
      img.crossOrigin = 'anonymous';
      img.onload = () => {
        setImage(img);
        setZoom(1);
        setOffset({ x: 0, y: 0 });
      };
      img.src = currentAvatarUrl;
    }
    if (!open) resetState();
  }, [open, currentAvatarUrl, resetState]);

  const loadImage = useCallback((file: File) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const img = new Image();
      img.onload = () => {
        setImage(img);
        setZoom(1);
        setOffset({ x: 0, y: 0 });
      };
      img.src = e.target?.result as string;
    };
    reader.readAsDataURL(file);
  }, []);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file && file.type.startsWith('image/')) {
      loadImage(file);
    }
    if (e.target) e.target.value = '';
  };

  // Draw the preview
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    ctx.clearRect(0, 0, CANVAS_SIZE, CANVAS_SIZE);
    ctx.fillStyle = 'hsl(var(--muted))';
    ctx.fillRect(0, 0, CANVAS_SIZE, CANVAS_SIZE);

    if (image) {
      const aspect = image.width / image.height;
      let drawW: number, drawH: number;

      if (aspect >= 1) {
        drawH = CANVAS_SIZE * zoom;
        drawW = drawH * aspect;
      } else {
        drawW = CANVAS_SIZE * zoom;
        drawH = drawW / aspect;
      }

      const drawX = (CANVAS_SIZE - drawW) / 2 + offset.x;
      const drawY = (CANVAS_SIZE - drawH) / 2 + offset.y;

      ctx.save();
      ctx.beginPath();
      ctx.arc(CANVAS_SIZE / 2, CANVAS_SIZE / 2, CANVAS_SIZE / 2, 0, Math.PI * 2);
      ctx.clip();
      ctx.drawImage(image, drawX, drawY, drawW, drawH);
      ctx.restore();
    }

    ctx.beginPath();
    ctx.arc(CANVAS_SIZE / 2, CANVAS_SIZE / 2, CANVAS_SIZE / 2 - 1, 0, Math.PI * 2);
    ctx.strokeStyle = 'hsl(var(--border))';
    ctx.lineWidth = 2;
    ctx.stroke();
  }, [image, zoom, offset]);

  // Mouse/Touch drag handlers
  const handlePointerDown = (e: React.PointerEvent) => {
    if (!image) return;
    setDragging(true);
    setDragStart({ x: e.clientX - offset.x, y: e.clientY - offset.y });
    (e.target as HTMLElement).setPointerCapture(e.pointerId);
  };

  const handlePointerMove = (e: React.PointerEvent) => {
    if (!dragging) return;
    setOffset({
      x: e.clientX - dragStart.x,
      y: e.clientY - dragStart.y,
    });
  };

  const handlePointerUp = () => {
    setDragging(false);
  };

  const handleSave = async () => {
    if (!image) return;
    setSaving(true);

    // Render to output canvas
    const outCanvas = document.createElement('canvas');
    outCanvas.width = OUTPUT_SIZE;
    outCanvas.height = OUTPUT_SIZE;
    const ctx = outCanvas.getContext('2d');
    if (!ctx) return;

    const scale = OUTPUT_SIZE / CANVAS_SIZE;
    const aspect = image.width / image.height;
    let drawW: number, drawH: number;

    if (aspect >= 1) {
      drawH = OUTPUT_SIZE * zoom;
      drawW = drawH * aspect;
    } else {
      drawW = OUTPUT_SIZE * zoom;
      drawH = drawW / aspect;
    }

    const drawX = (OUTPUT_SIZE - drawW) / 2 + offset.x * scale;
    const drawY = (OUTPUT_SIZE - drawH) / 2 + offset.y * scale;

    ctx.beginPath();
    ctx.arc(OUTPUT_SIZE / 2, OUTPUT_SIZE / 2, OUTPUT_SIZE / 2, 0, Math.PI * 2);
    ctx.clip();
    ctx.drawImage(image, drawX, drawY, drawW, drawH);

    outCanvas.toBlob(
      async (blob) => {
        if (blob) {
          try {
            await onSave(blob);
            onOpenChange(false);
          } catch {
            // error handled by parent
          }
        }
        setSaving(false);
      },
      'image/png',
      1
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{t('seek.avatar.editPhoto')}</DialogTitle>
        </DialogHeader>

        <div className="flex flex-col items-center gap-4">
          {/* Canvas preview */}
          <div
            className="rounded-full overflow-hidden cursor-grab active:cursor-grabbing border-2 border-border bg-muted"
            style={{ width: CANVAS_SIZE, height: CANVAS_SIZE, touchAction: 'none' }}
            onPointerDown={handlePointerDown}
            onPointerMove={handlePointerMove}
            onPointerUp={handlePointerUp}
          >
            <canvas
              ref={canvasRef}
              width={CANVAS_SIZE}
              height={CANVAS_SIZE}
              className="block"
            />
          </div>

          {!image && (
            <div className="text-center space-y-3">
              <p className="text-sm text-muted-foreground">
                {t('seek.avatar.uploadPrompt')}
              </p>
              <Button
                variant="outline"
                onClick={() => fileInputRef.current?.click()}
              >
                <Upload className="w-4 h-4 me-2" />
                {t('seek.avatar.choosePhoto')}
              </Button>
            </div>
          )}

          {image && (
            <>
              {/* Zoom slider */}
              <div className="flex items-center gap-3 w-full max-w-[280px]">
                <ZoomOut className="w-4 h-4 text-muted-foreground shrink-0" />
                <Slider
                  value={[zoom]}
                  onValueChange={([v]) => setZoom(v)}
                  min={0.5}
                  max={3}
                  step={0.05}
                  className="flex-1"
                />
                <ZoomIn className="w-4 h-4 text-muted-foreground shrink-0" />
              </div>

              {/* Action buttons */}
              <div className="flex gap-2">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    setZoom(1);
                    setOffset({ x: 0, y: 0 });
                  }}
                >
                  <RotateCcw className="w-4 h-4 me-1" />
                  {t('seek.avatar.reset')}
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => fileInputRef.current?.click()}
                >
                  <Camera className="w-4 h-4 me-1" />
                  {t('seek.avatar.changePhoto')}
                </Button>
              </div>
            </>
          )}

          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            className="hidden"
            onChange={handleFileChange}
          />
        </div>

        <DialogFooter className="gap-2 sm:gap-0">
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            {t('common.cancel')}
          </Button>
          <Button onClick={handleSave} disabled={!image || saving}>
            {saving ? t('seek.avatar.saving') : t('seek.avatar.savePhoto')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
